import { Box, Table, TableBody, TableHead } from '@mui/material'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FetchLicensedDirectorBillingDetailsResponse } from '../../../../swagger'
import {
  TableHeaderForPDF,
  TableHeaderForPDFProps,
} from '../../../Table/TableHeaderForPDF'
import {
  BreakdownLedgerForPDF,
  BreakDownLedgerPDFVariants,
} from '../BillingPDF/BreakdownLedgerForPDF'
import { styles } from '../BillingPDF/licensingBillingPdfStyles'
import { LicensingFeeSubTotalBreakdown } from '../BillingPDF/LicensingFeeSubTotalBreakdown'
import { SemesterOneSubtotalBreakdown } from '../BillingPDF/SemesterOneSubtotalBreakdown'
import { SemesterTwoSubtotalBreakdown } from '../BillingPDF/SemesterTwoSubtotalBreakdown'
import { prepareLicensingData } from './prepareLicensingData'
import {
  convertTwoDecimalStringToBig,
  formatBigFixedTwoDecimals,
} from '../../../../utils/bigJSHelper'

export interface LicensingBreakdownProps {
  licensing: FetchLicensedDirectorBillingDetailsResponse
}

export const LcdLicensingBreakdownForPDF: React.FC<LicensingBreakdownProps> = ({
  licensing,
}) => {
  const { t } = useTranslation()
  const currencyCode = licensing.director.totalOwed.currencyCode
  const isChallenge = licensing.programType.includes('Challenge')

  const tableHeaders: TableHeaderForPDFProps['tableHeaders'] = [
    {
      label: t('LcdLicensingBreakdown.Header.FeeName', 'Fee Name'),
      align: 'left',
      cssProps: styles.tableHeaderCell,
    },
    {
      label: t('LcdLicensingBreakdown.Header.FeeAmount', 'Fee Amount'),
      align: 'right',
      cssProps: styles.tableHeaderCell,
    },
    {
      label: t(
        'LcdLicensingBreakdown.Header.NumberOfStudents',
        '# of Students'
      ),
      align: 'right',
      cssProps: styles.tableHeaderCell,
    },
    {
      label: t('LcdLicensingBreakdown.Header.DirectorOwes', 'Director Owes CC'),
      align: 'right',
      cssProps: styles.tableHeaderCell,
    },
  ]

  const breakdown = prepareLicensingData(licensing.director.breakdown)

  return (
    <Box style={styles.table}>
      <Table style={styles.licensingPaymentBreakdownTable}>
        <TableHead>
          <TableHeaderForPDF tableHeaders={tableHeaders} />
        </TableHead>

        <TableBody>
          <LicensingFeeSubTotalBreakdown
            licensingFeeSubtotal={breakdown.invitationSubTotal}
            licensingFeeFirstStudent={{
              feeAmount: breakdown.firstSpotInvitation.amount.amount,
              numberOfStudents: breakdown.firstSpotInvitation.count,
              studentInfoSubTotal: breakdown.invitationFirstStudent,
              currencyCode,
            }}
            licensingFeeAdditionalStudent={{
              feeAmount: breakdown.additionalSpotsInvitation.amount.amount,
              numberOfStudents: breakdown.additionalSpotsInvitation.count,
              studentInfoSubTotal: breakdown.invitationAdditionalStudent,
              currencyCode,
            }}
            subHeaderLabel={t(
              'LcdLicensingBreakdown.SubHeader.InvitationLicenseFee',
              'Invitation License Fee'
            )}
            displaySubTotalLabel
            subTotalCellCssProps={{
              marginBottom: '12px',
            }}
          />

          <SemesterOneSubtotalBreakdown
            semester1Subtotal={breakdown.firstSemesterSubTotal}
            semester1FirstStudent={{
              feeAmount: breakdown.semester1FirstStudentsInfo.amount.amount,
              numberOfStudents: breakdown.semester1FirstStudentsInfo.count,
              studentInfoSubTotal: breakdown.firstSemesterFirstStudent,
              currencyCode,
            }}
            semester1AdditionalStudent={{
              feeAmount: breakdown.semester1AdditionalStudentInfo.amount.amount,
              numberOfStudents: breakdown.semester1AdditionalStudentInfo.count,
              studentInfoSubTotal: breakdown.firstSemesterAdditionalStudent,
              currencyCode,
            }}
            isChallenge
            subHeaderLabel={
              isChallenge
                ? t(
                    'LcdLicensingBreakdown.SubHeader.SemesterOneLicenseFee',
                    '1st Semester License Fee'
                  )
                : t(
                    'LcdLicensingBreakdown.SubHeader.SemesterOneSubHeaderNonChallenge',
                    'License Fee'
                  )
            }
            displaySubTotalLabel
            subTotalCellCssProps={{
              marginBottom: '12px',
            }}
          />

          <SemesterTwoSubtotalBreakdown
            semester2Subtotal={breakdown.secondSemesterSubTotal}
            semester2FirstStudent={{
              feeAmount: breakdown.semester2FirstStudentsInfo.amount.amount,
              numberOfStudents: breakdown.semester2FirstStudentsInfo.count,
              studentInfoSubTotal: breakdown.secondSemesterFirstStudent,
              currencyCode,
            }}
            semester2AdditionalStudent={{
              feeAmount: breakdown.semester2AdditionalStudentInfo.amount.amount,
              numberOfStudents: breakdown.semester2AdditionalStudentInfo.count,
              studentInfoSubTotal: breakdown.secondSemesterAdditionalStudent,
              currencyCode,
            }}
            isChallenge={isChallenge}
            subHeaderLabel={t(
              'LcdLicensingBreakdown.SubHeader.SemesterTwoLicenseFee',
              '2nd Semester License Fee'
            )}
            displaySubTotalLabel
            subTotalCellCssProps={{
              marginBottom: '12px',
            }}
          />

          <BreakdownLedgerForPDF
            variant={BreakDownLedgerPDFVariants.Balance}
            totalOwed={licensing.director.totalOwed.amount}
            totalPaid={licensing.director.totalPaid.amount}
            balance={formatBigFixedTwoDecimals(
              convertTwoDecimalStringToBig(
                licensing.director.totalOwed.amount
              ).minus(licensing.director.totalPaid.amount)
            )}
            licensingDue={licensing.director.licensingDue.amount}
            currencyCode={currencyCode}
            programName={licensing.programType}
            paymentList={[]}
            balanceLabel={t(
              'LcdLicensingBreakdown.SubHeader.Balance.Label',
              'Balance'
            )}
          />
        </TableBody>
      </Table>
    </Box>
  )
}
